import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse, MFARequiredResponse, LoginForm, RegisterForm } from '../types';
import apiService from '../services/api';

export type LoginResult =
  | { mfa_required: false }
  | { mfa_required: true; mfa_token: string };

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginForm) => Promise<LoginResult>;
  loginWithOTP: (mfa_token: string, otp: string, remember_device: boolean) => Promise<void>;
  register: (userData: RegisterForm) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const storedUser = apiService.getCurrentUserFromStorage();
      if (storedUser && apiService.isAuthenticated()) {
        // Verify the token is still valid by fetching current user
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      // Clear invalid auth data
      apiService.logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginForm): Promise<LoginResult> => {
    setIsLoading(true);
    try {
      const result = await apiService.login(credentials);
      if ((result as MFARequiredResponse).mfa_required) {
        return { mfa_required: true, mfa_token: (result as MFARequiredResponse).mfa_token };
      }
      setUser((result as AuthResponse).user);
      return { mfa_required: false };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithOTP = async (mfa_token: string, otp: string, remember_device: boolean): Promise<void> => {
    setIsLoading(true);
    try {
      const result = await apiService.verifyOTP(mfa_token, otp, remember_device);
      setUser(result.user);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterForm) => {
    setIsLoading(true);
    try {
      await apiService.register(userData);
      // Don't auto-login — user completes MFA on the login tab
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await apiService.logout();
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  };

  const logoutAll = async () => {
    setIsLoading(true);
    try {
      await apiService.logoutAll();
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginWithOTP,
    register,
    logout,
    logoutAll,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
